import { debounce } from 'lodash';
import userStateService from "@/common/userState.service.js";

const MAX_BUFFER_ITEMS = 200;
const SCROLL_BOTTOM_THRESHOLD = 300;
const SCROLL_TOP_THRESHOLD = 80;

export default {
  props: {
    serverDataOptions: {
      type: Object,
      default: undefined
    },

    persistentFilters: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      serverLoading: false,
      serverItems: [],
      serverFilters: this.filtersValues,
      serverFilterLabels: {},
      queryParams: {
        page: 1,
        perPage: this.itemsPerPage
      },
      searchString: this.searchValue,
      isServerFetchingNow: false,
      lastPage: 1,
      fetchedPages: [],
      pageItemCounts: {},
      canLoadPreviousPage: true,
      lastScrollTop: 0,
    }
  },

  mounted() {
    this.searchString = this.persistentFilters ?
      userStateService.getState(`table-state.${this.name}.searchString`, '') :
      '';

    this.$emit('search', this.searchString);

    this.serverFilters = this.persistentFilters ?
      userStateService.getState(`table-state.${this.name}.serverFilters`, {}) :
      {};

    this.serverFilterLabels = this.persistentFilters ?
      userStateService.getState(`table-state.${this.name}.filterLabels`, {}) :
      {};

    this.$emit('filters-change', this.serverFilters);

    this.options = userStateService.getState(`table-state.${this.name}.options`, {
      groupBy: this.groupBy || [],
      groupDesc: this.groupDesc || [],
      itemsPerPage: this.itemsPerPage,
      multiSort: this.multiSort || true,
      mustSort: this.mustSort || false,
      page: 1,
      sortBy: this.sortBy || [],
      sortDesc: this.sortDesc || [false],
    });
  },

  methods: {
    getScrollWrapper() {
      return this.$refs.table?.$el?.querySelector('.v-data-table__wrapper');
    },

    rememberPageItems(page, items) {
      this.pageItemCounts[page] = items.length;
    },

    getPageItemCount(page) {
      return this.pageItemCounts[page] || this.itemsPerPage;
    },

    trimOldestPage(scrolledEl) {
      const oldestPage = this.fetchedPages[0];
      if (!oldestPage) {
        return 0;
      }

      const removedCount = this.getPageItemCount(oldestPage);
      const scrollTop = scrolledEl?.scrollTop || 0;
      let removedHeight = 0;

      if (scrolledEl) {
        const rows = this.$refs.table.$el.querySelectorAll('tbody tr');

        for (let i = 0; i < removedCount && rows[i]; i += 1) {
          removedHeight += rows[i].offsetHeight;
        }
      }

      this.serverItems = this.serverItems.slice(removedCount);
      delete this.pageItemCounts[oldestPage];
      this.fetchedPages = this.fetchedPages.slice(1);

      if (scrolledEl) {
        this.$nextTick(() => {
          scrolledEl.scrollTop = Math.max(0, scrollTop - removedHeight);
        });
      }

      return removedCount;
    },

    trimNewestPage() {
      const newestPage = this.fetchedPages[this.fetchedPages.length - 1];
      if (!newestPage) {
        return 0;
      }

      const removedCount = this.getPageItemCount(newestPage);

      this.serverItems = this.serverItems.slice(0, -removedCount);
      delete this.pageItemCounts[newestPage];
      this.fetchedPages = this.fetchedPages.slice(0, -1);

      return removedCount;
    },

    trimBufferFromEndWhileOverflow() {
      while (
        this.serverItems.length > MAX_BUFFER_ITEMS &&
        this.fetchedPages.length > 1
      ) {
        this.trimNewestPage();
      }
    },

    trimBufferFromStartWhileOverflow(scrolledEl) {
      while (
        this.serverItems.length > MAX_BUFFER_ITEMS &&
        this.fetchedPages.length > 1
      ) {
        this.trimOldestPage(scrolledEl);
      }
    },

    fetchServerData(overwriteTableItems = true) {
      if (!this.isServerFetching()) {
        return;
      }

      return new Promise((resolve, reject) => {
        this.serverLoading = true;

        if (overwriteTableItems) {
          this.queryParams.page = 1;
        }

        const extraParams = { ...(this.serverDataOptions.params || {}) };
        const { filters: extraFilters, ...restExtraParams } = extraParams;

        const params = {
          ...restExtraParams,
          ...this.queryParams,
          search: this.searchString,
          filters: {
            ...(extraFilters || {}),
            ...(this.serverFilters || {}),
          },
        };

        this.$store
          .dispatch(this.serverDataOptions.method, params)
          .then((response) => {
            const currentPage = response.meta.current_page;
            const pageItems = response.data || [];

            if (overwriteTableItems) {
              this.serverItems = pageItems;
              this.fetchedPages = [currentPage];
              this.pageItemCounts = {};
              this.canLoadPreviousPage = true;
              this.rememberPageItems(currentPage, pageItems);

              const wrapper = this.getScrollWrapper();
              if (wrapper) {
                wrapper.scrollTop = 0;
              }

              this.$emit('overwritten-table-items');
            } else if (!this.fetchedPages.includes(currentPage)) {
              this.rememberPageItems(currentPage, pageItems);
              this.fetchedPages.push(currentPage);
              this.fetchedPages.sort((a, b) => a - b);
            }

            this.localTotalItems = response.meta.total;
            this.lastPage = response.meta.last_page;

            this.$emit('fetched-server-data', {
              params: params,
              totalItems: this.localTotalItems
            });

            resolve(response);
          }).catch((error) => {
            reject(error);
          }).finally(() => {
            this.serverLoading = false;
          });
      });
    },

    serverHandleOptionsUpdate(options) {
      userStateService.setState(
        `table-state.${this.name}.options`,
        options
      );

      this.queryParams = {...options};

      this.fetchServerData();
    },

    serverHandleSearchInput: debounce(function (searchString, options) {
      let newSearchString = searchString.length > 2 ? searchString : '';

      if (this.searchString !== newSearchString) {
        userStateService.setState(
          `table-state.${this.name}.searchString`,
          newSearchString
        );

        this.searchString = newSearchString;

        this.queryParams = {...options};

        this.fetchServerData();
      }
    }, 1500),

    serverHandleFiltersChange(filters, options) {
      userStateService.setState(
        `table-state.${this.name}.serverFilters`,
        filters
      );

      this.serverFilters = filters;

      this.pruneFilterLabels(filters);

      this.queryParams = {...options};

      this.fetchServerData();
    },

    pruneFilterLabels(filters) {
      const prunedLabels = {};

      Object.keys(this.serverFilterLabels || {}).forEach((key) => {
        const value = filters[key];
        const hasValue = Array.isArray(value)
          ? value.length > 0
          : value !== undefined && value !== null && value !== '';

        if (hasValue) {
          prunedLabels[key] = this.serverFilterLabels[key];
        }
      });

      this.serverFilterLabels = prunedLabels;

      userStateService.setState(
        `table-state.${this.name}.filterLabels`,
        prunedLabels
      );
    },

    serverHandleScrollToBottom() {
      if (this.isServerFetchingNow) {
        return;
      }

      const lastFetchedPage = this.fetchedPages[this.fetchedPages.length - 1];
      if (!lastFetchedPage || lastFetchedPage >= this.lastPage) {
        return;
      }

      const nextPage = lastFetchedPage + 1;
      if (this.fetchedPages.includes(nextPage)) {
        return;
      }

      const scrolledEl = this.getScrollWrapper();
      if (!scrolledEl) {
        return;
      }

      this.isServerFetchingNow = true;
      this.queryParams.page = nextPage;

      this.fetchServerData(false)
        .then((response) => {
          const pageItems = response.data || [];
          this.serverItems = [...this.serverItems, ...pageItems];
          this.trimBufferFromStartWhileOverflow(scrolledEl);
        })
        .catch(() => {})
        .finally(() => {
          this.isServerFetchingNow = false;
        });
    },

    handleServerTableScroll(scrolledEl) {
      if (!scrolledEl || this.isServerFetchingNow) {
        return;
      }

      const scrollTop = scrolledEl.scrollTop;
      const isScrollingUp = scrollTop < this.lastScrollTop;

      if (scrollTop > SCROLL_BOTTOM_THRESHOLD) {
        this.canLoadPreviousPage = true;
      }

      if (
        isScrollingUp &&
        scrollTop < SCROLL_TOP_THRESHOLD &&
        this.canLoadPreviousPage &&
        this.fetchedPages[0] > 1
      ) {
        this.serverHandleScrollToTop();
      }

      const scrollBottom = scrolledEl.scrollHeight - (scrollTop + scrolledEl.offsetHeight);

      if (
        scrollBottom < SCROLL_BOTTOM_THRESHOLD &&
        this.fetchedPages[this.fetchedPages.length - 1] < this.lastPage
      ) {
        this.serverHandleScrollToBottom();
      }

      this.lastScrollTop = scrollTop;
    },

    serverHandleScrollToTop() {
      if (this.isServerFetchingNow) {
        return;
      }

      const firstFetchedPage = this.fetchedPages[0];
      if (!firstFetchedPage || firstFetchedPage <= 1) {
        return;
      }

      const previousPage = firstFetchedPage - 1;
      if (this.fetchedPages.includes(previousPage)) {
        return;
      }

      const scrolledEl = this.getScrollWrapper();
      if (!scrolledEl) {
        return;
      }

      this.isServerFetchingNow = true;
      this.canLoadPreviousPage = false;
      this.queryParams.page = previousPage;

      const previousScrollTop = scrolledEl.scrollTop;
      const previousScrollHeight = scrolledEl.scrollHeight;

      this.fetchServerData(false)
        .then((response) => {
          const pageItems = response.data || [];
          this.serverItems = [...pageItems, ...this.serverItems];

          return this.$nextTick().then(() => {
            scrolledEl.scrollTop = previousScrollTop + (scrolledEl.scrollHeight - previousScrollHeight);
            this.trimBufferFromEndWhileOverflow();
          });
        })
        .catch(() => {})
        .finally(() => {
          this.isServerFetchingNow = false;
        });
    },

    deleteServerItem(key) {
      this.serverItems = this.serverItems.filter((item) => item[this.itemKey] !== key);
    },

    isServerFetching() {
      return this.serverDataOptions && this.serverDataOptions.method;
    }
  }
};
